/**
 * F5 — singleton toast bus. QueryClient.onError / MutationCache.onError — вне React,
 * не могут вызвать useToast() hook. Через bus push-им события, ToastProvider
 * subscribe-ится и рендерит.
 *
 * Deduplication: одинаковые (title, description) в пределах 2s — агрегируются
 * в один toast с префиксом «×N».
 */

import type { ToastItem, ToastVariant } from './Toast';

export type ToastEmit = {
  variant: ToastVariant;
  title: string;
  description?: string;
  /** S-FIX-5-FE.9.1: trace_id отдельной строкой в toast. */
  traceId?: string;
  durationMs?: number;
  /** notification-center F6 — deep-link по клику на тост (client-side nav). */
  href?: string;
  /**
   * Пак context-menu (F9) — обратимое действие прямо в тосте: «Архивировано ·
   * Вернуть». Без этого единственный путь отката архива — уйти в архив-вид и
   * искать задачу там, то есть отката практически нет.
   * Клик по действию закрывает тост.
   */
  action?: { label: string; onClick: () => void };
};

/**
 * F6 — client-side deep-link по клику на тост. Bus живёт вне React и вне
 * RouterProvider, поэтому навигация — через History API + popstate, который
 * TanStack Router слушает (SPA-переход без полной перезагрузки).
 */
export function toastNavigate(href: string): void {
  if (typeof window === 'undefined') return;
  window.history.pushState({}, '', href);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

type Listener = (t: ToastEmit) => void;

const listeners = new Set<Listener>();

export function subscribeToast(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const DEDUPE_WINDOW_MS = 2000;
const recentEmits = new Map<string, { count: number; firstAt: number }>();

function keyOf(t: ToastEmit): string {
  return `${t.variant}::${t.title}::${t.description ?? ''}`;
}

export function emitToast(t: ToastEmit): void {
  const k = keyOf(t);
  const now = Date.now();
  const prev = recentEmits.get(k);
  if (prev && now - prev.firstAt < DEDUPE_WINDOW_MS) {
    prev.count += 1;
    // Агрегируем — в UI обновить последнее сообщение с «×N».
    const aggregated: ToastEmit = {
      ...t,
      title: `${t.title} ×${prev.count}`,
    };
    for (const l of listeners) l(aggregated);
    return;
  }
  recentEmits.set(k, { count: 1, firstAt: now });
  setTimeout(() => recentEmits.delete(k), DEDUPE_WINDOW_MS);
  for (const l of listeners) l(t);
}

/** Для совместимости с F5 paln: toast.push(...). */
export const toast = {
  push(t: ToastEmit): void {
    emitToast(t);
  },
};

export type { ToastItem };
